import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  composeSyncValidators,
  required,
  validEmail,
} from 'coupon-common/src/utils';

import messages from './messages';

import ICON_MAIL from '../../assets/icons/icon-envelope.svg';
import ICON_KEY from '../../assets/icons/icon-key.svg';

function GoogleIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
      <path d="M18.62 9.7c0-.62-.06-1.22-.16-1.8H9.5v3.42h5.13a4.39 4.39 0 01-1.9 2.88v2.4h3.08c1.8-1.66 2.84-4.1 2.84-6.9z" fill="#4285F4"/>
      <path d="M9.5 19c2.57 0 4.72-.85 6.3-2.3l-3.08-2.4c-.85.57-1.94.91-3.22.91-2.48 0-4.58-1.67-5.33-3.92H1v2.47A9.5 9.5 0 009.5 19z" fill="#34A853"/>
      <path d="M4.17 11.29a5.7 5.7 0 010-3.58V5.24H1a9.5 9.5 0 000 8.52l3.17-2.47z" fill="#FBBC05"/>
      <path d="M9.5 3.79c1.4 0 2.65.48 3.64 1.42l2.73-2.73C14.21.98 12.06 0 9.5 0A9.5 9.5 0 001 5.24l3.17 2.47C4.92 5.46 7.02 3.79 9.5 3.79z" fill="#EA4335"/>
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
      <circle cx="9.5" cy="9.5" r="9.5" fill="#1877F2" />
      <path d="M12.6 9.7h-2.03v6.7H7.98V9.7H6.5V7.4h1.48V5.94c0-1.61.72-2.84 3-2.84.65 0 1.34.1 1.34.1v2.06h-.99c-.98 0-1.3.53-1.3 1.24V7.4h2.2l-.33 2.3z" fill="#fff"/>
    </svg>
  );
}

/* ── Inline eye icons — no external asset dependency ── */
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="2.4" stroke="#8A8A8A" strokeWidth="1.4" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6c1.5 0 2.8.4 3.9 1M17 9s-1.1 2.2-3.1 3.8M9 15c-5 0-8-6-8-6a14.5 14.5 0 013-3.8"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 9a2 2 0 002.8 1.8M11 9a2 2 0 00-2.7-1.9" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M1 1l16 16" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

const LoginForm = ({ intl, submitting, handleSubmit, submitError }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form onSubmit={handleSubmit}>
      {/* <button type="button" className="auth-social-btn" disabled>
        <GoogleIcon /> Login with Google
      </button>
      <button type="button" className="auth-social-btn" disabled>
        <FacebookIcon /> Login with Facebook
      </button>

      <div className="auth-divider"><span>OR</span></div> */}

      <Field
        name="email"
        validate={composeSyncValidators(required, validEmail())}
      >
        {({ input, meta }) => (
          <label className="auth-field">
            <img src={ICON_MAIL} alt="" className="auth-field-icon" />
            <div>
              <div className="auth-field-label">
                {intl.formatMessage(messages.emailLabelText)}
              </div>
              <input
                {...input}
                type="email"
                placeholder="example@gmail.com"
                autoComplete="email"
              />
            </div>
            {meta.touched && meta.error && (
              <span className="auth-field-error">{meta.error}</span>
            )}
          </label>
        )}
      </Field>

      <Field name="password" validate={required}>
        {({ input, meta }) => (
          <label className="auth-field">
            <img src={ICON_KEY} alt="" className="auth-field-icon" />
            <div>
              <div className="auth-field-label">
                {intl.formatMessage(messages.passwordLabelText)}
              </div>
              <input
                {...input}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              type="button"
              className="auth-field-toggle"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
            {meta.touched && meta.error && (
              <span className="auth-field-error">{meta.error}</span>
            )}
          </label>
        )}
      </Field>

      {submitError && <div className="auth-error">{submitError}</div>}

      <div className="auth-row-between">
        <label className="auth-checkbox">
          <input type="checkbox" />
          <span className="auth-checkbox-box" />
          <FormattedMessage {...messages.rememberMeCheckboxText} />
        </label>
        <Link to="/forgot-password" className="auth-link">
          <FormattedMessage {...messages.forgotPasswordLinkText} />
        </Link>
      </div>

      <button
        type="submit"
        className="auth-btn auth-btn-solid auth-btn-block"
        disabled={submitting}
      >
        {submitting ? '...' : <FormattedMessage {...messages.loginButtonText} />}
      </button>

      <p className="auth-footer-text">
        <FormattedMessage {...messages.goToSignUpLinkText} />{' '}
        <Link to="/signup/business" className="auth-link">Register</Link>
      </p>
    </form>
  );
};

LoginForm.propTypes = {
  intl: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  submitError: PropTypes.string,
};

export default injectIntl(LoginForm);